import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import makeAjaxRequest from "helpers/makeAjaxRequest";
import { rootUrl } from "../actions/constants";

const selectTable = state => state.attributeSetup;

export const asBrowseSelector = createSelector(
  [selectTable],
  attributeSetup => attributeSetup.browse || {}
);

export const asSelectedAttributeSelector = createSelector(
  [selectTable],
  attributeSetup => attributeSetup.selectedAttribute || {}
);

export const asAnsEnumerationSelector = createSelector(
  [selectTable],
  attributeSetup => attributeSetup.ansEnumeration || []
);
export const asAnsEnumCodeSelector = createSelector(
  [selectTable],
  attributeSetup => {
    if (Array.isArray(attributeSetup.ansEnumeration)) {
      return attributeSetup.ansEnumeration.map(x => x.enumCode);
    }
    return [];
  }
);
export const parentEnumerationSelector = createSelector(
  [selectTable],
  attributeSetup => attributeSetup.parentEnumeration || []
);

export const asSelectedAnsEnumeration = createSelector(
  [selectTable],
  attributeSetup => attributeSetup.selectedAnsEnumeration || {}
);

export const selectedIsEditing = createSelector(
  [selectTable],
  attributeSetup => attributeSetup.isEditing || false
);

export const defaultRowSelector = createSelector(
  [selectTable],
  attributeSetup => attributeSetup.defaultRow || {}
);
export const personOrOrgTypeLovSelector = createSelector(
  [selectTable],
  attributeSetup => attributeSetup.personOrOrgTypeLov || []
);

const initialState = {
  selectedAttribute: null,
  browse: {
    data: [],
    loading: false,
    error: null
  },
  ansEnumeration: null,
  parentEnumeration: null,
  selectedAnsEnumeration: null,
  isEditing: false,
  personOrOrgTypeLov: null,
  defaultRow: {
    "attrCode": "",
    "ownerType": "",
    "object": "",
    "attrName": "",
    "displayOrder": "",
    "attrClass": "",
    "isRequired": false,
    "defaultValue": null,
    "status": "ACTIVE",
    "maxLength": "",
    "isMultiSelect": false,
    "isValidateEnum": false,
    "isDateSensitive": false,
    "isEffectiveDateRequired": false,
    "monthsEffective": "",
    "helpText": ""
  }
};

const sortBrowserData = (data) => {
  return data.sort((a, b) => {
    const fields = ["object", "ownerType", "attrCode"];
    for (let field of fields) {
      if (a[field] < b[field]) return -1;
      if (a[field] > b[field]) return 1;
    }
    return 0;
  });
};

export const fetchASData = createAsyncThunk(
  "attribute/browse",
  async (attrName, { rejectWithValue }) => {
    try {
      const { meta, data } = await makeAjaxRequest.postWithAuth(`${rootUrl}/admin/attribute/browse`, {});
      const sortedData = sortBrowserData(data);
      if (attrName) {
        const index = sortedData.findIndex(obj => obj.attrName === attrName);
        if (index !== -1) {
          const [selectedObj] = sortedData.splice(index, 1);
          sortedData.unshift(selectedObj);
        }
      }
      return { meta, data: sortedData };
    } catch (err) {
      return rejectWithValue(err.response ? err.response.data : err.message);
    }
  }
);

export const fetchPersonOrOrgTypeLov = createAsyncThunk(
  "attribute/PersonOrOrgTypeLov",
  async (objType, { rejectWithValue }) => {
    try {
      const response = await makeAjaxRequest.fetchWithAuth(`${rootUrl}/admin/attribute/${objType}/persorgtypelov`);
      return response.sort((a, b) => a.label.localeCompare(b.label));
    } catch (err) {
      return rejectWithValue(err.response ? err.response.data : err.message);
    }
  }
);

export const fetchAnsEnumeration = createAsyncThunk(
  "attribute/answerenumeration",
  async (params, { rejectWithValue }) => {
    const { attrCode, attrClass } = params;
    if (!attrCode) {
      return [];
    }

    try {
      const response = await makeAjaxRequest.fetchWithAuth(
        `${rootUrl}/admin/attribute/${attrCode}/answerenumeration/${attrClass}`,
        {}
      );
      return response;
    } catch (err) {
      return rejectWithValue(err.response ? err.response.data : err.message);
    }
  }
);
export const fetchParentEnumeration = createAsyncThunk(
  "attribute/parentEnumeration",
  async (params, { rejectWithValue }) => {
    const { attrCode, attrClass } = params;
    if (!attrCode) {
      return [];
    }
    try {
      const response = await makeAjaxRequest.fetchWithAuth(
        `${rootUrl}/admin/attribute/${attrCode}/answerenumeration/${attrClass}`,
        {}
      );
      if (Array.isArray(response)) {
        return response.map(ans => ({ value: ans.enumCode, label: ans.enumStr }));
      } else {
        return [];
      }
    } catch (err) {
      return rejectWithValue(err.response ? err.response.data : err.message);
    }
  }
);

const attributeSetupSlice = createSlice({
  name: "attributeSetup",
  initialState,
  reducers: {
    saveSelectedAttribute: (state, action) => {
      state.selectedAttribute = action.payload;
    },
    saveSelectedAnsEnumeration: (state, action) => {
      state.selectedAnsEnumeration = action.payload;
    },
    setIsEditingFlag: (state, action) => {
      state.isEditing = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchASData.pending, (state) => {
        state.browse.loading = true;
      })
      .addCase(fetchASData.fulfilled, (state, action) => {
        state.browse.loading = false;
        state.browse.data = action.payload.data;
      })
      .addCase(fetchASData.rejected, (state, action) => {
        state.browse.loading = false;
        state.browse.error = action.error.message;
      });
    builder.addCase(fetchAnsEnumeration.fulfilled, (state, action) => {
      state.ansEnumeration = action.payload;
    });
    builder.addCase(fetchParentEnumeration.fulfilled, (state, action) => {
      state.parentEnumeration = action.payload;
    });
    builder.addCase(fetchPersonOrOrgTypeLov.fulfilled, (state, action) => {
      state.personOrOrgTypeLov = action.payload;
    });
  }
});
export const { saveSelectedAttribute, saveSelectedAnsEnumeration, setIsEditingFlag } = attributeSetupSlice.actions;
export default attributeSetupSlice.reducer;
